/** Plain-language formatting. Coordinators read dates, not ISO strings. */

export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const today = new Date();
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(date, today)) return 'Today';

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Yesterday';

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (sameDay(date, tomorrow)) return 'Tomorrow';

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

export function formatRange(start?: string | null, end?: string | null): string {
  if (!start) return '—';
  return end ? `${formatTime(start)}–${formatTime(end)}` : formatTime(start);
}

/** "3 hours ago", "in 40 minutes" — how a queue age actually reads. */
export function formatRelative(iso?: string | null): string {
  if (!iso) return '';
  const deltaMinutes = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const abs = Math.abs(deltaMinutes);
  const past = deltaMinutes < 0;

  if (abs < 1) return 'just now';
  const say = (value: number, unit: string) =>
    past ? `${value} ${unit}${value === 1 ? '' : 's'} ago` : `in ${value} ${unit}${value === 1 ? '' : 's'}`;

  if (abs < 60) return say(abs, 'minute');
  const hours = Math.round(abs / 60);
  if (hours < 24) return say(hours, 'hour');
  return say(Math.round(hours / 24), 'day');
}

/** `urgent_concern` → `Urgent concern`. */
export function humanise(value?: string | null): string {
  if (!value) return '';
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function fullAddress(address: Record<string, string | null>): string {
  return (
    [address.line1, address.line2, address.city, address.region, address.postal_code]
      .filter(Boolean)
      .join(', ') || 'Address not provided'
  );
}

/**
 * The same instant on the agency's clock, not the reader's.
 *
 * Everything above formats in the browser's zone, which is right for a
 * coordinator sitting in their own office and wrong for scheduling. A visit
 * at 09:00 in the client's kitchen has to read 09:00 on the screen that books
 * it, whatever zone the laptop is in — otherwise someone working away from the
 * office rosters a caregiver an hour out and the screen agrees with them.
 */
export function formatTimeIn(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
}

/** "Mon 13 Oct", on the agency's clock. */
export function formatDayIn(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone,
  });
}

/** The agency's local date as YYYY-MM-DD, for date inputs and API ranges. */
export function localDateIn(date: Date, timeZone: string): string {
  // en-CA gives ISO-ordered parts, which is the shortest correct way to get a
  // calendar date in another zone without pulling in a date library.
  return date.toLocaleDateString('en-CA', { timeZone });
}
